<script lang="ts">
    import { twMerge } from 'tailwind-merge';
    import { getContext } from 'svelte';
    import type { FormColorType } from '../types';
    import Label from './Label.svelte';

    // properties forwarding
    export let color: FormColorType = 'secondary';
    export let custom = false;
    export let inline = false;
    export let group: (string | number)[] = [];
    export let value: string | number = 'on';
    export let checked: boolean | undefined = undefined;
    export let spacing = 'mr-2';

    // tinted if put in component having its own background
    let background: boolean = getContext('background');

    const colorClasses: Record<FormColorType, string> = {
        primary: 'text-gray-900 checked:bg-primary-500 indeterminate:bg-primary-500 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        secondary: 'text-gray-900 checked:bg-secondary-900 indeterminate:bg-secondary-900 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        red: 'text-gray-900 checked:bg-red-600 indeterminate:bg-red-600 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        green: 'text-gray-900 checked:bg-green-600 indeterminate:bg-green-600 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        purple: 'text-gray-900 checked:bg-purple-600 indeterminate:bg-purple-600 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        teal: 'text-gray-900 checked:bg-teal-600 indeterminate:bg-teal-600 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        yellow: 'text-gray-900 checked:bg-yellow-400 indeterminate:bg-yellow-400 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        orange: 'text-gray-900 checked:bg-orange-500 indeterminate:bg-orange-500 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        blue: 'text-gray-900 checked:bg-blue-600 indeterminate:bg-blue-600 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        accentGreen: 'text-gray-900 checked:bg-accent-green-500 indeterminate:bg-accent-green-500 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        accentBlue: 'text-gray-900 checked:bg-accent-blue-500 indeterminate:bg-accent-blue-500 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        accentYellow: 'text-gray-900 checked:bg-accent-yellow-500 indeterminate:bg-accent-yellow-500 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        accentPurple: 'text-gray-900 checked:bg-accent-purple-500 indeterminate:bg-accent-purple-500 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
        gray: 'text-gray-900 checked:bg-gray-500 indeterminate:bg-gray-500 disabled:indeterminate:bg-gray-400 disabled:checked:bg-gray-400 focus:ring-gray-100',
    };
    const labelClass = (inline: boolean, extraClass: string) => twMerge(inline ? 'inline-flex' : 'flex', 'items-center', extraClass);
    const inputClass = (custom: boolean, color: FormColorType, rounded: boolean, tinted: boolean, extraClass: string) =>
        twMerge(
            'w-[1.125rem] h-[1.125rem] bg-gray-50 disabled:bg-gray-100 border-gray-200 dark:ring-offset-gray-800 appearance-none border checked:border-none focus:ring shrink-0',
            'cursor-pointer disabled:cursor-not-allowed accent-primary-600',
            spacing,
            tinted ? 'dark:bg-gray-600 dark:border-gray-500' : 'dark:bg-gray-700 dark:border-gray-600',
            custom && 'sr-only peer',
            rounded && 'rounded',
            colorClasses[color],
            extraClass,
        );

    $: labelDefaultClass = twMerge('select-none font-medium text-gray-800', checked && 'font-bold', $$props.disabled ? 'cursor-not-allowed' : 'cursor-pointer');

    // react on external group changes
    function init(_: HTMLElement, _group: (string | number)[]) {
        if (checked === undefined) checked = _group.includes(value);
        onChange();

        return {
            update(_group: (string | number)[]) {
                checked = _group.includes(value);
            },
        };
    }

    function onChange() {
        // There's a bug in Svelte and bind:group is not working with wrapped checkbox
        // This workaround is taken from:
        // https://svelte.dev/repl/de117399559f4e7e9e14e2fc9ab243cc?version=3.12.1
        const index = group.indexOf(value);

        if (checked === undefined) checked = index >= 0;

        if (checked) {
            if (index < 0) {
                group.push(value);
                group = group;
            }
        } else {
            if (index >= 0) {
                group.splice(index, 1);
                group = group;
            }
        }
    }
</script>

<Label class={labelClass(inline, twMerge(labelDefaultClass, $$props.class))} show={!!$$slots.default}>
    <input
        use:init={group}
        type="checkbox"
        bind:checked
        on:keyup
        on:keydown
        on:keypress
        on:focus
        on:blur
        on:click
        on:mouseover
        on:mouseenter
        on:mouseleave
        on:paste
        on:change={onChange}
        on:change
        {value}
        {...$$restProps}
        class={twMerge(spacing, inputClass(custom, color, true, background, $$slots.default || $$props.class))}
    />
    <slot {checked} />
</Label>

<!--
  @component
  ## 체크박스 (Checkbox)

  사용자가 하나 이상의 옵션을 선택할 수 있는 체크박스 컴포넌트입니다.

  ### 주요 기능
  - 기본 체크박스
  - 비활성화 상태
  - 중간 상태 (Indeterminate)
  - 그룹 선택 (여러 개 체크박스를 배열로 관리)
  - 인라인 배치
  - 다양한 색상 테마
  - 커스텀 디자인

  ### Props
  @prop color: FormColorType = 'secondary' - 체크박스 색상 (primary, secondary, red, green, blue, purple, orange, yellow, teal, accentGreen, accentBlue, accentYellow)
  @prop custom: boolean = false - 커스텀 디자인 사용 여부 (true일 경우 기본 체크박스 숨김, peer 스타일 활용)
  @prop inline: boolean = false - 인라인 배치 여부
  @prop group: (string | number)[] = [] - 그룹으로 관리할 체크박스 값들의 배열
  @prop value: string | number = 'on' - 체크박스의 값 (그룹 사용 시 필수)
  @prop checked: boolean | undefined = undefined - 체크 상태
  @prop spacing: string = 'mr-2' - 체크박스와 레이블 사이 간격

  ### Events
  - on:keyup - 키를 뗄 때
  - on:keydown - 키를 누를 때
  - on:keypress - 키를 누르고 있을 때
  - on:focus - 포커스를 받을 때
  - on:blur - 포커스를 잃을 때
  - on:click - 클릭했을 때
  - on:mouseover - 마우스가 올라갔을 때
  - on:mouseenter - 마우스가 진입했을 때
  - on:mouseleave - 마우스가 떠났을 때
  - on:paste - 붙여넣기 했을 때
  - on:change - 값이 변경될 때

  ### 사용 예제

  **기본 사용**
  ```svelte
  <script>
    import { Checkbox } from '$component/basic'
    let checked = false;
  </script>

  <Checkbox bind:checked>기본 체크박스</Checkbox>
  <Checkbox checked>체크된 상태</Checkbox>
  <Checkbox disabled>비활성화</Checkbox>
  ```

  **그룹으로 사용**
  ```svelte
  <script>
    import { Checkbox } from '$component/basic'
    let selectedItems = [1, 3];
  </script>

  <Checkbox bind:group={selectedItems} value={1}>항목 1</Checkbox>
  <Checkbox bind:group={selectedItems} value={2}>항목 2</Checkbox>
  <Checkbox bind:group={selectedItems} value={3}>항목 3</Checkbox>
  ```

  **색상 변경**
  ```svelte
  <Checkbox color="primary">Primary</Checkbox>
  <Checkbox color="green">Green</Checkbox>
  <Checkbox color="red">Red</Checkbox>
  ```

  **중간 상태 (전체 선택)**
  ```svelte
  <script>
    let items = [1, 2];
    $: allChecked = items.length === 3;
    $: indeterminate = items.length > 0 && !allChecked;
  </script>

  <Checkbox
    bind:checked={allChecked}
    bind:indeterminate
    on:click={() => items = allChecked ? [] : [1, 2, 3]}
  >
    전체 선택
  </Checkbox>
  ```
-->
